import { useState, type FormEvent, type ReactElement } from 'react';
import { messageForError } from '@proctoring/api-client';
import { createInterviewerRequestSchema, type User, type UserStatus } from '@proctoring/shared';
import { useCreateInterviewer, useInterviewers } from '../api/queries.js';
import { FullPageMessage } from '../components/FullPageMessage.js';

interface DraftState {
  fullName: string;
  email: string;
}

const EMPTY: DraftState = { fullName: '', email: '' };

/** Field-name → label, so a schema issue can be reported against the input the admin sees. */
const FIELD_LABELS: Record<string, string> = {
  email: 'Email',
  fullName: 'Full name',
};

const STATUS_LABELS: Record<UserStatus, string> = {
  invited: 'Invited',
  active: 'Active',
  locked: 'Locked',
};

/**
 * Colour follows *whether the admin need do anything*: an account still waiting on its
 * setup link is the one worth noticing, a locked one is the one worth worrying about.
 */
const STATUS_CLASSES: Record<UserStatus, string> = {
  invited: 'border-severity-warning/40 bg-severity-warning/10 text-severity-warning',
  active: 'border-session-border bg-session-panel-raised text-session-muted',
  locked: 'border-severity-critical/40 bg-severity-critical/10 text-severity-critical',
};

/**
 * Validates a draft against the shared contract.
 *
 * Reusing `createInterviewerRequestSchema` rather than hand-rolling checks means the email
 * rule the API enforces and the one the form shows can never drift apart.
 */
function validate(
  draft: DraftState,
): { ok: true; value: DraftState } | { ok: false; message: string } {
  const candidate = { email: draft.email.trim(), fullName: draft.fullName.trim() };
  const parsed = createInterviewerRequestSchema.safeParse(candidate);
  if (parsed.success) return { ok: true, value: parsed.data };
  const issue = parsed.error.issues[0];
  const label = FIELD_LABELS[String(issue?.path[0] ?? '')] ?? 'Form';
  return { ok: false, message: `${label}: ${issue?.message ?? 'is invalid'}` };
}

function InterviewerRow({ person }: { person: User }): ReactElement {
  return (
    <li
      data-testid="interviewer-row"
      className="flex items-center justify-between gap-4 border-b border-session-border px-5 py-4 last:border-b-0"
    >
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-session-text">{person.fullName}</p>
        <p className="truncate text-xs text-session-dim">{person.email}</p>
      </div>
      <span
        data-status={person.status}
        className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_CLASSES[person.status]}`}
      >
        {STATUS_LABELS[person.status]}
      </span>
    </li>
  );
}

export function InterviewersPage(): ReactElement {
  const { data, isPending, error } = useInterviewers();
  const create = useCreateInterviewer();
  const [draft, setDraft] = useState<DraftState>(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);
  const [invited, setInvited] = useState<string | null>(null);

  async function onCreate(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const result = validate(draft);
    if (!result.ok) {
      setFormError(result.message);
      setInvited(null);
      return;
    }
    setFormError(null);
    try {
      const user = await create.mutateAsync(result.value);
      setDraft(EMPTY);
      setInvited(user.email);
    } catch (cause) {
      setInvited(null);
      setFormError(messageForError(cause));
    }
  }

  if (isPending) return <FullPageMessage title="Loading interviewers…" />;
  if (error) {
    return <FullPageMessage title="Could not load interviewers" detail={messageForError(error)} />;
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-6 py-10">
      <h1 className="text-xl font-semibold tracking-tight text-session-text">Interviewers</h1>
      <p className="mt-1 text-sm text-session-muted">
        Interviewers are invited by email and finish setting up their own password. An account can
        be assigned to interviews as soon as it is invited.
      </p>

      <section className="panel mt-8 p-5" aria-labelledby="new-interviewer">
        <h2 id="new-interviewer" className="text-sm font-semibold text-session-text">
          Invite an interviewer
        </h2>
        <form onSubmit={onCreate} className="mt-4 space-y-3" noValidate>
          {formError ? (
            <p role="alert" className="form-error">
              {formError}
            </p>
          ) : null}
          {invited ? (
            <p
              role="status"
              className="rounded-md border border-session-border bg-session-panel-raised px-3 py-2 text-sm text-session-muted"
            >
              Invitation sent to {invited}. With the console mailer the setup link is printed in the
              API log.
            </p>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="new-full-name" className="field-label">
                Full name
              </label>
              <input
                id="new-full-name"
                value={draft.fullName}
                onChange={(event) => setDraft({ ...draft, fullName: event.target.value })}
                placeholder="Ingrid Interviewer"
                className="field-input"
              />
            </div>
            <div>
              <label htmlFor="new-email" className="field-label">
                Email
              </label>
              <input
                id="new-email"
                type="email"
                value={draft.email}
                onChange={(event) => setDraft({ ...draft, email: event.target.value })}
                placeholder="ingrid@example.com"
                className="field-input"
              />
            </div>
          </div>
          <button type="submit" disabled={create.isPending} className="btn-primary">
            {create.isPending ? 'Sending invitation…' : 'Send invitation'}
          </button>
        </form>
      </section>

      <section className="mt-6" aria-labelledby="all-interviewers">
        <h2 id="all-interviewers" className="mb-3 text-sm font-semibold text-session-text">
          All interviewers
        </h2>
        {data.length === 0 ? (
          <p className="panel px-5 py-6 text-sm text-session-muted">
            No interviewer accounts yet. Invite the first one above before scheduling an interview.
          </p>
        ) : (
          <ul className="panel overflow-hidden">
            {data.map((person) => (
              <InterviewerRow key={person.id} person={person} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
